import React, { useEffect, useRef, useState } from 'react';
import { Link, Element } from 'react-scroll';

// Process steps data
const processSteps = [
  {
    id: 1,
    title: "Sign Up in Minutes",
    description: "Create your account with just a few clicks. All you need is a valid ID and proof of address to get started.",
    image: "/images/login.png"
  },
  {
    id: 2,
    title: "Browse Investment Options",
    description: "Explore a curated selection of money market funds tailored to diverse financial goals, whether you're saving for a rainy day or planning long-term growth.",
    image: "/images/browser-market.png"
  },
  {
    id: 3,
    title: "Make Your First Investment",
    description: "With as little as 100 dollars, you can start investing. Use our user-friendly interface to select the fund that aligns with your needs and make secure payments.",
    image: "/images/invest-today.png"
  },
  {
    id: 4,
    title: "Watch Your Wealth Grow",
    description: "Monitor your portfolio in real-time. We provide transparent updates on your investments' performance and offer insights to help you make informed decisions.",
    image: "/images/investment-growing.png"
  },
  {
    id: 5,
    title: "Withdraw Anytime",
    description: "Enjoy the flexibility to access your funds whenever you need them, without unnecessary delays or penalties.",
    image: "/images/withdraw.png"
  }
];

// Inline styles object
const styles = {
  servicesSection: {
    backgroundColor: '#fff',
    position: 'relative',
    zIndex: 1,
    overflow: 'hidden',
    padding: '2rem 0',
  },
  stepsContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem 1rem',
    perspective: '1000px', // 3D perspective for rotational effects
  },
  processStep: (index, isVisible) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4rem',
    opacity: isVisible ? 1 : 0,
    transform: isVisible 
      ? 'translateZ(0) rotateY(0)' 
      : index % 2 === 0
        ? 'translateZ(-100px) rotateY(-20deg)'
        : 'translateZ(-100px) rotateY(20deg)',
    transformOrigin: index % 2 === 0 ? 'left center' : 'right center',
    transition: 'opacity 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    transformStyle: 'preserve-3d',
  }),
  processStepReverse: {
    flexDirection: 'row-reverse',
  },
  processStepContent: (isVisible) => ({
    flex: 1,
    padding: '0 2rem',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
    transition: 'opacity 0.6s ease, transform 0.6s ease',
    transitionDelay: '0.2s',
  }),
  processStepImage: (isVisible, index) => {
    const isOdd = index % 2 === 1;
    return {
      flex: 1,
      borderRadius: '10px',
      overflow: 'hidden',
      boxShadow: isVisible ? '0 15px 30px rgba(0, 0, 0, 0.2)' : '0 5px 15px rgba(0, 0, 0, 0.1)',
      opacity: isVisible ? 1 : 0,
      transform: isVisible 
        ? 'scale(1) rotate(0deg)' 
        : isOdd
          ? 'scale(0.9) rotate(-3deg)'
          : 'scale(0.9) rotate(3deg)', 
      transition: 'all 1s cubic-bezier(0.23, 1, 0.32, 1)',
      transitionDelay: '0.3s',
      position: 'relative',
    };
  },
  // Removed the imageOverlay style entirely
  img: {
    width: '100%',
    height: 'auto',
    display: 'block',
    transition: 'transform 0.5s ease',
  },
  imgHover: {
    transform: 'scale(1.05)',
  },
  processStepNumber: (isVisible) => ({
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#007bff',
    marginBottom: '1rem',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 0.6s ease, transform 0.6s ease',
    transitionDelay: '0.4s',
    position: 'relative',
  }),
  numberGlow: (isVisible) => ({
    position: 'absolute',
    top: '-10px',
    left: '-10px',
    right: '-10px',
    bottom: '-10px',
    background: 'radial-gradient(circle, rgba(0,123,255,0.2) 0%, rgba(0,123,255,0) 70%)',
    borderRadius: '50%',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'scale(1)' : 'scale(0.5)',
    transition: 'opacity 0.8s ease, transform 0.8s ease',
    transitionDelay: '0.6s',
    zIndex: -1,
  }),
  processStepTitle: (isVisible) => ({
    fontSize: '1.5rem',
    marginBottom: '1rem',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(15px)',
    transition: 'opacity 0.6s ease, transform 0.6s ease',
    transitionDelay: '0.5s',
  }),
  processStepDescription: (isVisible) => ({
    color: '#666',
    lineHeight: 1.6,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
    transition: 'opacity 0.6s ease, transform 0.6s ease',
    transitionDelay: '0.6s',
  }),
  progressLine: (isVisible, isLast) => !isLast ? ({
    position: 'absolute',
    bottom: '-2rem',
    left: '2.5rem',
    width: '2px',
    height: isVisible ? '4rem' : '0',
    backgroundColor: '#007bff',
    transition: 'height 0.8s ease',
    transitionDelay: '0.7s',
  }) : null,
  // For section header with animation
  sectionHeader: (isVisible) => ({
    textAlign: 'center',
    marginBottom: '4rem',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(-30px)',
    transition: 'opacity 0.8s ease, transform 0.8s ease',
  }),
  subhead: {
    fontSize: '1.2rem',
    color: '#007bff',
    marginBottom: '0.5rem',
  },
  display1: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    position: 'relative',
  },
  headerUnderline: (isVisible) => ({
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    width: isVisible ? '80px' : '0',
    height: '3px',
    backgroundColor: '#007bff',
    transform: 'translateX(-50%)',
    transition: 'width 1s ease',
    transitionDelay: '0.4s',
  }),
};

const Services = () => {
  const [hoveredStep, setHoveredStep] = useState(null);
  const [visibleSteps, setVisibleSteps] = useState({});
  const [headerVisible, setHeaderVisible] = useState(false);
  const stepRefs = useRef([]);
  const headerRef = useRef(null);

  // Setup Intersection Observer to detect when elements come into view
  useEffect(() => {
    const observerOptions = {
      root: null, // use the viewport
      rootMargin: '0px',
      threshold: 0.15, // trigger when 15% of the element is visible
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.dataset.type === 'header') {
            setHeaderVisible(true);
          } else if (entry.target.dataset.stepId) {
            // Get the step ID from the data attribute
            const stepId = parseInt(entry.target.dataset.stepId);
            setVisibleSteps(prev => ({ ...prev, [stepId]: true }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe header
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    // Observe all the step refs
    stepRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });

    // Cleanup
    return () => {
      if (headerRef.current) {
        observer.unobserve(headerRef.current);
      }
      
      if (stepRefs.current) {
        stepRefs.current.forEach(ref => {
          if (ref) observer.unobserve(ref);
        });
      }
    };
  }, []);

  // Initialize the step refs array
  useEffect(() => {
    stepRefs.current = stepRefs.current.slice(0, processSteps.length);
  }, [processSteps.length]);

  return (
    <section id="services" style={styles.servicesSection}>
      <div 
        ref={headerRef}
        data-type="header"
        style={styles.sectionHeader(headerVisible)}
      >
        <div>
          <h3 style={styles.subhead}>How it works</h3>
          <h1 style={styles.display1}>
            Using Laki is easy
            <span style={styles.headerUnderline(headerVisible)}></span>
          </h1>
        </div>
      </div>

      <div style={styles.stepsContainer}>
        {processSteps.map((step, index) => {
          const isVisible = visibleSteps[step.id] || false;
          const isOdd = index % 2 === 1;
          const isLast = index === processSteps.length - 1;
          
          return (
            <div 
              key={step.id}
              ref={el => stepRefs.current[index] = el}
              data-step-id={step.id}
              style={{
                ...styles.processStep(index, isVisible),
                ...(isOdd ? styles.processStepReverse : {}),
                transitionDelay: `${index * 0.1}s`,
                position: 'relative',
              }}
              onMouseEnter={() => setHoveredStep(step.id)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <div style={styles.processStepContent(isVisible)}>
                <div style={styles.processStepNumber(isVisible)}>
                  {step.id}
                  <div style={styles.numberGlow(isVisible)}></div>
                </div>
                <div>
                  <h4 style={styles.processStepTitle(isVisible)}>{step.title}</h4>
                  <p style={styles.processStepDescription(isVisible)}>{step.description}</p>
                </div>
                {!isLast && <div style={styles.progressLine(isVisible, isLast)}></div>}
              </div>
              <div style={styles.processStepImage(isVisible, index)}>
                <img 
                  style={{
                    ...styles.img,
                    ...(hoveredStep === step.id ? styles.imgHover : {})
                  }}
                  src={`${process.env.PUBLIC_URL}${step.image}`}
                  alt={step.title} 
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = `https://via.placeholder.com/600x450?text=${step.title.replace(/\s+/g, '+')}`;
                  }}
                />
                {/* Removed the div with imageOverlay style */}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Services;